import React, { useEffect, useState } from "react";

import { Chip, CircularProgress } from "@mui/material";
import AirplanemodeActiveRounded from "@mui/icons-material/AirplanemodeActiveRounded";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";

import { useSetRecoilState } from "recoil";
import { snackbarData } from "recoilState/GlobalState";

import { migraUsuarioPasoGet, migraVueloGet } from "services/services-mia/migra";
import { transformDateTime } from "services/Local-services";
import { ESTADO_LABELS, mapMigraUsuarioPasoRow } from "pages/migra/utils/usuarioPaso";
import XGrid from "components/UI/Grid/XGrid";

import styles from "./VueloDetalleModal.module.scss";

const PASAJEROS_GRID_HEIGHT = 320;

const TIPO_CHIP = {
    E: { label: "Entrada", color: "#166534", background: "#f0fdf4", border: "#86efac" },
    S: { label: "Salida", color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
};

const VUELO_ESTADO_CHIP = {
    A: { label: "Abierto", color: "#166534", background: "#f0fdf4", border: "#86efac" },
    C: { label: "Cerrado", color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
};

const PASAJEROS_ESTADO_CHIP = {
    P: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
    V: { color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
    C: { color: "#166534", background: "#f0fdf4", border: "#86efac" },
    A: { color: "#92400e", background: "#fffbeb", border: "#fcd34d" },
    B: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
};

function EstadoVueloChip({ estado }) {
    const chip = VUELO_ESTADO_CHIP[estado] ?? { label: estado, color: "#374151", background: "#f3f4f6", border: "#d1d5db" };
    return (
        <Chip
            label={chip.label}
            size="small"
            sx={{ color: chip.color, backgroundColor: chip.background, border: `1px solid ${chip.border}`, fontWeight: 600 }}
        />
    );
}

function TipoChip({ tipo }) {
    if (!tipo) return <span className={styles.value}>-</span>;
    const chip = TIPO_CHIP[tipo] ?? { label: tipo, color: "#374151", background: "#f3f4f6", border: "#d1d5db" };
    return (
        <Chip
            label={chip.label}
            size="small"
            sx={{ color: chip.color, backgroundColor: chip.background, border: `1px solid ${chip.border}`, fontWeight: 600 }}
        />
    );
}

function PasajeroEstadoChip({ estado }) {
    const palette = PASAJEROS_ESTADO_CHIP[estado] ?? PASAJEROS_ESTADO_CHIP.B;
    return (
        <Chip
            label={ESTADO_LABELS[estado] ?? estado}
            size="small"
            sx={{ color: palette.color, backgroundColor: palette.background, border: `1px solid ${palette.border}`, fontWeight: 600 }}
        />
    );
}

const PASAJEROS_COLUMNS = [
    { field: "apellidoYNombre", headerName: "Apellido y Nombre", flex: 1, minWidth: 220 },
    { field: "usuarioNroDeDocumentoX", headerName: "Documento", width: 150 },
    {
        field: "migraUsuarioPasoEstado",
        headerName: "Estado",
        width: 150,
        renderCell: ({ value }) => <PasajeroEstadoChip estado={value} />,
    },
    { field: "migraUsuarioPasoFechaCheckin", headerName: "Check-in", width: 170, format: "dateTime" },
];

export default function VueloPasajerosModal({ row, credentials }) {
    const [loading, setLoading] = useState(true);
    const [loadingPasajeros, setLoadingPasajeros] = useState(true);
    const [vuelo, setVuelo] = useState(null);
    const [pasajeros, setPasajeros] = useState([]);
    const [pasajeroSeleccionado, setPasajeroSeleccionado] = useState({ status: false, row: {} });
    const setSnackbarInfo = useSetRecoilState(snackbarData);

    useEffect(() => {
        (async () => {
            const response = await migraVueloGet({ migraVueloId: row.migraVueloId }, credentials);

            if (response?.status?.code === 1) {
                setVuelo((response.result || [])[0] || null);
            } else {
                setSnackbarInfo({
                    message: response?.status?.errmsg || "No se pudo obtener el detalle del vuelo",
                    severity: "error",
                    open: true,
                });
            }
            setLoading(false);
        })();
    }, [row.migraVueloId, credentials, setSnackbarInfo]);

    useEffect(() => {
        (async () => {
            const response = await migraUsuarioPasoGet(
                { migraVueloId: row.migraVueloId, page: null, pageSize: null },
                credentials
            );

            if (response?.status?.code === 1) {
                setPasajeros((response.result || []).map(mapMigraUsuarioPasoRow));
            } else {
                setSnackbarInfo({
                    message: response?.status?.errmsg || "No se pudieron obtener los pasajeros",
                    severity: "error",
                    open: true,
                });
            }
            setLoadingPasajeros(false);
        })();
    }, [row.migraVueloId, credentials, setSnackbarInfo]);

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
                <CircularProgress />
            </div>
        );
    }

    if (!vuelo) {
        return (
            <div style={{ textAlign: "center", padding: "2rem", color: "#4b5563" }}>
                No se encontró información del vuelo.
            </div>
        );
    }

    const pasajerosPorEstado = Object.entries(vuelo.pasajerosPorEstado || {});

    return (
        <div className={styles.container}>
            <div className={styles.sectionsRowSplit}>
                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <AirplanemodeActiveRounded /> Datos del vuelo
                    </h4>
                    <div className={styles.passengersWrap}>
                        <table className={styles.passengersTable}>
                            <thead>
                                <tr>
                                    <th>Paso</th>
                                    <th>Tipo</th>
                                    <th>Fecha</th>
                                    <th>Identificación</th>
                                    <th>{vuelo.migraVueloTipo === "S" ? "Destino" : "Origen"}</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{vuelo.migraPasoNombre}</td>
                                    <td><TipoChip tipo={vuelo.migraVueloTipo} /></td>
                                    <td>{vuelo.migraVueloFecha ? transformDateTime(vuelo.migraVueloFecha) : "-"}</td>
                                    <td>{vuelo.migraVueloIdentificacion}</td>
                                    <td>{vuelo.migraVueloOrigenDestino}</td>
                                    <td><EstadoVueloChip estado={vuelo.migraVueloEstado} /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <GroupsOutlined /> Pasajeros por estado
                    </h4>
                    <div className={styles.passengersWrap}>
                        <table className={styles.passengersTable}>
                            <thead>
                                <tr>
                                    <th>Estado</th>
                                    <th>Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pasajerosPorEstado.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>Sin pasajeros asignados</td>
                                    </tr>
                                ) : (
                                    pasajerosPorEstado.map(([estado, cantidad]) => (
                                        <tr key={estado}>
                                            <td><PasajeroEstadoChip estado={estado} /></td>
                                            <td>{cantidad}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                    <PeopleAltOutlined /> Pasajeros
                </h4>
                {pasajeros.length === 0 && !loadingPasajeros ? (
                    <span className={styles.value}>Sin pasajeros asignados</span>
                ) : (
                    <XGrid
                        rows={pasajeros}
                        titles={PASAJEROS_COLUMNS}
                        loading={loadingPasajeros}
                        height={PASAJEROS_GRID_HEIGHT}
                        hideFooter={pasajeros.length <= 10}
                        secondarySelected={pasajeroSeleccionado}
                        setSecondarySelected={setPasajeroSeleccionado}
                    />
                )}
            </section>
        </div>
    );
}
